/* eslint-disable @tencent/i18next/no-literal-string */
import { lowMemoryMode } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/chart-component/utils';

type Task = () => Promise<any>;

interface ScheduleTask {
    id: string;
    task: Task;
    resolve: (value: any) => void;
    reject: (value: any) => void;
    memory: number;
    priority: number;
}

type Mode = 'memory' | 'count';
interface TaskQueueManagerProps {
    max?: number;
    interval?: number;
    memory?: number
    mode: Mode;
}

export const Priority = {
    LOW: -10000,         // 低优先级
    NORMAL: 0,    // 正常优先级
    HIGH: 10000,    // 高优先级
};

// 默认最大并行任务数
const NORMAL_MAX_SCHEDULING_NUMBER = 100;
const LOW_MODE_MAX_SCHEDULING_NUMBER = 5;
const DEFAULT_MAX_SCHEDULING_NUMBER = 10;

// 默认最大内存占用
const DEFAULT_MAX_MEMORY = 5 * 1024 * 1024;

// 默认任务执行间隔
const NORMAL_SCHEDULE_INTERVAL = 0;
const LOW_MODE_SCHEDULE_INTERVAL = 50;
const DEFAULT_SCHEDULE_INTERVAL = 50;

export class TaskQueueManager {
    protected static instance?: TaskQueueManager;
    public static getInstance(): TaskQueueManager {
        if (!TaskQueueManager.instance) TaskQueueManager.instance = new TaskQueueManager({
            mode: 'count',
            max: lowMemoryMode ? LOW_MODE_MAX_SCHEDULING_NUMBER : NORMAL_MAX_SCHEDULING_NUMBER,
            interval: lowMemoryMode ? LOW_MODE_SCHEDULE_INTERVAL : NORMAL_SCHEDULE_INTERVAL,
        });
        return TaskQueueManager.instance;
    }

    // 正在执行的任务
    private schedulingTasks: ScheduleTask[] = [];
    // 等待执行的任务
    private inQueueTasks: ScheduleTask[] = [];
    private schedulingTotalMemory = 0;
    // 最大同时执行的任务个数
    private readonly maximumSchedulingNumber: number;
    // 最大内存占用
    private readonly maxMemoryLimit: number;
    // 任务执行间隔
    private readonly scheduleInterval: number;
    private readonly mode: Mode;

    public constructor(props: TaskQueueManagerProps) {
        const { max, interval, memory, mode } = props;
        this.mode = mode;
        this.maximumSchedulingNumber = max || DEFAULT_MAX_SCHEDULING_NUMBER;
        this.scheduleInterval = interval || DEFAULT_SCHEDULE_INTERVAL;
        this.maxMemoryLimit = memory || DEFAULT_MAX_MEMORY;
    }

    public runTask(task: Task, opt?: { memory?: number; priority?: number, name?: string }): Promise<any> {
        const { memory, priority } = opt || {};
        return new Promise((resolve, reject) => {
            const scheduleTask: ScheduleTask =  {
                id: `${opt?.name}task-${Math.random().toString(16)}-${Date.now()}`,
                task,
                resolve,
                reject,
                memory: memory || 0,
                priority: priority || Priority.NORMAL,
            };

            this.add(scheduleTask);
        });
    }

    private add(scheduleTask: ScheduleTask) {
        this.inQueue(scheduleTask);

        this.scheduleNextTask();
    }

    private shouldScheduleImmediately(task: ScheduleTask) {
        if (this.mode === 'count') {
            const shouldSchedule = this.schedulingTasks.length < this.maximumSchedulingNumber;
            return shouldSchedule;
        }

        // 如果当前没有任务，或者加起来内存小于限制就执行（存在单个任务超限制的，就只能一个个执行）
        const shouldSchedule = this.schedulingTotalMemory === 0
            || this.schedulingTotalMemory + task.memory <= this.maxMemoryLimit;
        return shouldSchedule;
    }

    private async completeTask(scheduledTask: ScheduleTask) {
        await this.waitInterval();
        const taskIndex = this.schedulingTasks.findIndex(task => task.id === scheduledTask.id);
        if (taskIndex > -1) {
            this.schedulingTasks.splice(taskIndex, 1);
            this.releaseMemory(scheduledTask);
        }
        this.scheduleNextTask();
    }

    private scheduleNextTask() {
        const task = this.getNextTask();

        if (task && this.shouldScheduleImmediately(task)) {
            this.removeFormInQueueTasks(task);
            this.schedule(task);
        }
    }


    private inQueue(scheduleTask: ScheduleTask) {
        this.inQueueTasks.push(scheduleTask);
    }

    private removeFormInQueueTasks(scheduleTask: ScheduleTask) {
        const taskIndex = this.inQueueTasks.findIndex(task => task.id === scheduleTask.id);
        if (taskIndex > -1) {
            this.inQueueTasks.splice(taskIndex, 1);
        }
    }

    // 后续看需求是否需要支持retry，
    private async schedule(scheduleTask: ScheduleTask) {
        this.schedulingTasks.push(scheduleTask);
        this.increaseMemory(scheduleTask);

        try {
            const taskResult = await scheduleTask.task();
            scheduleTask.resolve(taskResult);
        } catch (err) {
            scheduleTask.reject(err);
        }

        this.completeTask(scheduleTask);
    }

    private increaseMemory(task: ScheduleTask) {
        if (this.mode === 'memory') {
            this.schedulingTotalMemory += task.memory;
        }
    }

    private releaseMemory(task: ScheduleTask) {
        if (this.mode === 'memory') {
            this.schedulingTotalMemory -= task.memory;
        }
    }

    private getNextTask(): ScheduleTask | null {
        let nextTask: ScheduleTask | null = null;

        this.inQueueTasks.forEach((task) => {
            if (!nextTask || task.priority > (nextTask as ScheduleTask).priority) {
                nextTask = task;
            }
        });

        return nextTask;
    }

    private waitInterval(): Promise<void> {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve();
            }, this.scheduleInterval);
        });
    }
}
